import { Badge, Group, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconCreditCard } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import { formatRupees } from '../api/subscription'
import { EmptyState } from '../components/EmptyState'
import type { WidgetRendererProps } from './registry'

type Due = {
  id: string
  name: string
  next_renewal_on: string
  amount_due_paise: number | null
  overdue: boolean
}

type Payload = {
  outstanding_paise: number
  due_soon_paise: number
  overdue_count: number
  overdue_paise: number
  due: Due[]
}

/**
 * What is due, on the dashboard.
 *
 * The overdue line comes first and in red, because that is the one that
 * costs something: a due date that passed with no payment recorded looks
 * exactly like a quiet month until somebody checks the vendor's console.
 */
export function SubscriptionsDueWidget({ payload }: WidgetRendererProps) {
  const data = payload.data as unknown as Payload

  return (
    <Stack gap="xs" h="100%">
      <Group gap="lg">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Due in 30 days
          </Text>
          <Text fw={700} size="lg" ff="monospace">
            {formatRupees(data.due_soon_paise)}
          </Text>
        </div>

        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Outstanding
          </Text>
          <Text fw={700} size="lg" ff="monospace">
            {formatRupees(data.outstanding_paise)}
          </Text>
        </div>

        {data.overdue_count > 0 && (
          <div>
            <Text size="xs" c="red" tt="uppercase" fw={600}>
              Overdue
            </Text>
            <Text fw={700} size="lg" ff="monospace" c="red">
              {formatRupees(data.overdue_paise)}
            </Text>
          </div>
        )}
      </Group>

      {data.due.length === 0 ? (
        <EmptyState
          compact
          topic="spending"
          icon={<IconCreditCard size={22} />}
          title="Nothing due"
          hint="No renewals within 30 days."
        />
      ) : (
        <Stack gap={4}>
          {data.due.map((row) => (
            <Group
              key={row.id}
              justify="space-between"
              wrap="nowrap"
              style={{ minWidth: 0 }}
            >
              <Link
                to="/subscription/$subscriptionId"
                search={{ tab: 'reports' }}
                params={{ subscriptionId: row.id }}
                style={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
              >
                <Group gap={6} wrap="nowrap">
                  {row.overdue && <IconAlertTriangle size={13} color="var(--mantine-color-red-6)" />}
                  <Text size="sm" truncate="end">
                    {row.name}
                  </Text>
                </Group>
              </Link>

              <Group gap={8} wrap="nowrap">
                <Text size="xs" c={row.overdue ? 'red' : 'dimmed'}>
                  {row.next_renewal_on}
                </Text>
                <Text size="sm" ff="monospace" fw={600}>
                  {row.amount_due_paise === null ? '-' : formatRupees(row.amount_due_paise)}
                </Text>
                {row.overdue && (
                  <Badge size="xs" color="red" variant="light">
                    Overdue
                  </Badge>
                )}
              </Group>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
